import { Component, computed, input, output, signal } from "@angular/core";

import type {
  EntityPerformanceModelType,
  EntityPeriodFilterModel,
  SelectedPeriodModelType,
  ValueText,
} from "../../../models";
import { ChartActionButtonsComponent } from "../../chart-action-buttons/chart-action-buttons";
import { EntityComparisonFiltersComponent } from "./entity-comparison-filters/entity-comparison-filters";
import { EntityPerformanceRankingComponent } from "./entity-performance-ranking/entity-performance-ranking";

@Component({
  selector: "adaa-entity-comparison-modal",
  imports: [EntityComparisonFiltersComponent, ChartActionButtonsComponent, EntityPerformanceRankingComponent],
  templateUrl: "./entity-comparison-modal.html",
  styleUrl: "./entity-comparison-modal.scss",
})
export class EntityComparisonModalComponent {
  title = input.required<string>();
  entities = input.required<EntityPerformanceModelType[]>();
  average = input<number | undefined>(undefined);
  selectedPeriod = input<SelectedPeriodModelType | undefined>(undefined);
  showEntityTypeAndSize = input<boolean>(false);
  /** Id of the wrapper the export buttons rasterise; must be unique per open popup. */
  elementId = input.required<string>();
  enableDownloadCSV = input<boolean>(false);
  csvRows = input<string[][]>();
  csvRowHeaders = input<string[]>();

  filterChanged = output<EntityPeriodFilterModel>();
  closed = output<void>();

  entityTypes = signal<ValueText[]>([]);
  entitySizes = signal<ValueText[]>([]);
  appliedFilter = signal<EntityPeriodFilterModel | undefined>(undefined);

  hasData = computed(() => this.entities().length > 0 || this.average() !== undefined);

  // The filter reports the period it applied; before anything is applied it is
  // still the period the host opened the modal with.
  periodLabel = computed(() => {
    const applied = this.appliedFilter();
    if (applied) return applied.chartTitle ?? "";

    const period = this.selectedPeriod();
    if (!period?.period) return "";
    return period.type === "year" ? `${period.period.year}` : `Q${period.period.quarter} ${period.period.year}`;
  });

  subtitle = computed(() => {
    const entityType = this.appliedFilter()?.entityType;
    const entitySize = this.appliedFilter()?.entitySize;

    const parts: string[] = [this.periodLabel()];
    if (entityType) parts.push(this.entityTypes().find((e) => e.value === entityType)?.text ?? "");
    if (entitySize) parts.push(this.entitySizes().find((e) => e.value === entitySize)?.text ?? "");

    return parts.filter((part) => part.length > 0).join(" - ");
  });

  public onFilterChanged(event: EntityPeriodFilterModel) {
    this.appliedFilter.set(event);
    this.filterChanged.emit(event);
  }
}
